import { useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GroupRow } from '@/components/GroupRow'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { SuggestEventSource } from '@/hooks/useEventSuggestions'
import type { Group } from '@/types'


function isValidUrl(value: string): boolean {
  try {
    return new URL(value.trim()).protocol === 'https:'
  } catch {
    return false
  }
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  groups: Group[]
  source: SuggestEventSource
  onSubmit: (url: string, groupIds: string[], source: SuggestEventSource) => Promise<void>
}

export function SuggestEventDialog({ open, onOpenChange, groups, source, onSubmit }: Props) {
  const [url, setUrl] = useState('')
  const [groupIds, setGroupIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [touched, setTouched] = useState(false)

  const valid = isValidUrl(url)
  const showError = touched && url.trim().length > 0 && !valid

  function reset() {
    setUrl('')
    setGroupIds([])
    setTouched(false)
  }

  function toggleGroup(id: string) {
    setGroupIds(prev => (prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (!valid || submitting) return
    setSubmitting(true)
    try {
      await onSubmit(url, groupIds, source)
      toast.success(
        fbt(
          "Thanks! We'll review it and add it shortly.",
          'Toast after successfully submitting an event suggestion',
        ),
      )
      reset()
      onOpenChange(false)
    } catch {
      // runMutation already surfaced the error toast.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <fbt desc="Dialog title for suggesting an event to be added to Beacon">
              Suggest an event
            </fbt>
          </DialogTitle>
          <DialogDescription>
            <fbt desc="Dialog subtitle explaining what to paste when suggesting an event">
              Paste a link to the event — a ticket page, festival site, or lineup post.
            </fbt>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Input
              autoFocus
              type="url"
              inputMode="url"
              placeholder="https://"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onBlur={() => setTouched(true)}
              aria-invalid={showError}
            />
            {showError && (
              <p className="text-sm text-destructive">
                <fbt desc="Validation error shown when the pasted event link is not a valid https URL">
                  That doesn't look like a valid link. It should start with https://
                </fbt>
              </p>
            )}
          </div>

          {groups.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                <fbt desc="Label above the optional group picker in the suggest event dialog">
                  Link it to a group once it's live (optional)
                </fbt>
              </p>
              {/* Capped so a user with many groups can't push the submit button
                  off-screen — the picker is optional and sits last. */}
              <div className="flex flex-col gap-2 max-h-56 overflow-y-auto">
                {groups.map(group => {
                  const selected = groupIds.includes(group.id)
                  return (
                    <GroupRow
                      key={group.id}
                      group={group}
                      selected={selected}
                      ariaPressed={selected}
                      onClick={() => toggleGroup(group.id)}
                      trailing={hasImage => (
                        <span
                          className={cn(
                            'shrink-0 size-5 rounded-full border flex items-center justify-center transition-colors',
                            selected
                              ? 'bg-primary border-primary text-primary-foreground'
                              : hasImage
                                ? 'border-on-image/60'
                                : 'border-border',
                          )}
                        >
                          {selected && <Check className="size-3" />}
                        </span>
                      )}
                    />
                  )
                })}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="submit" disabled={submitting || !valid}>
              {submitting
                ? fbt('Sending…', 'Button label while an event suggestion is being submitted')
                : fbt('Send suggestion', 'Button label to submit the suggest event form')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
